import { useState, useEffect } from 'react';
import { Search, Eye, Download, FilePlus2, Printer, Mail, X } from 'lucide-react';
import MonthRangePicker from '../../components/MonthRangePicker';
import { documentsApi } from '../../utils/api';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import PrintPreviewModal from '../../components/PrintPreviewModal';
import DocumentDetailModal from '../../components/DocumentDetailModal';
import useAuthStore from '../../store/authStore';

import useFiltroPeriodo from '../../hooks/useFiltroPeriodo';
// Notas de Débito (tipo 02). Tienen su pantalla propia, igual que las NC: no se
// mezclan con las facturas del Historial. Hoy nacen sobre todo al anular una NC
// ("Anular con ND" en Notas de Crédito).

const STATUS_CONFIG = {
  draft: { label: 'Borrador', color: 'bg-gray-100 text-gray-700' },
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
  processing: { label: 'Procesando', color: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Aceptada', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rechazada', color: 'bg-red-100 text-red-700' },
  error: { label: 'Error', color: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelada', color: 'bg-gray-200 text-gray-700' },
};

const TIPO_DOC_REF = { '01': 'FE', '02': 'ND', '03': 'NC', '04': 'TE' };

const CODIGO_REFERENCIA = {
  '01': 'Anula documento de referencia',
  '02': 'Corrige texto',
  '03': 'Corrige monto',
  '04': 'Referencia a otro documento',
  '05': 'Sustituye comprobante provisional',
  '99': 'Otros'
};

const formatCurrency = (amount, currency = 'CRC') => {
  const symbol = currency === 'USD' ? '$' : '₡';
  const v = parseFloat(amount) || 0;
  const sign = v < 0 ? '-' : '';
  return sign + symbol + new Intl.NumberFormat('es-CR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Math.abs(v));
};

const formatDate = (date) => {
  if (!date) return '-';
  const s = String(date);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-');
    return `${d}/${m}/${y}`;
  }
  return new Date(s).toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const montos = (doc) => ({
  moneda: doc.codigo_moneda || doc.codigoMoneda || 'CRC',
  subtotal: parseFloat(doc.total_venta_neta || doc.totales?.totalVentaNeta) || 0,
  impuestos: parseFloat(doc.total_impuesto || doc.totales?.totalImpuesto) || 0,
  total: parseFloat(doc.total_comprobante || doc.totales?.totalComprobante) || 0
});

export default function NotasDebito() {
  const { currentEmisor } = useAuthStore();
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [filtroFecha, setFiltroFecha] = useFiltroPeriodo('NotasDebito', () => {
    const n = new Date();
    const y = n.getFullYear();
    const m = String(n.getMonth() + 1).padStart(2, '0');
    const last = new Date(y, n.getMonth() + 1, 0).getDate();
    return { modo: 'mes', periodo: `${y}-${m}`, desde: `${y}-${m}-01`, hasta: `${y}-${m}-${String(last).padStart(2, '0')}` };
  });

  const cargarDocumentos = async () => {
    setLoading(true);
    try {
      const params = { page: 1, limit: 10000, tipo: '02', ligero: '1' }; // sin XML
      if (search) params.search = search;
      if (filtroFecha?.desde) params.desde = filtroFecha.desde;
      if (filtroFecha?.hasta) params.hasta = filtroFecha.hasta;
      const response = await documentsApi.list(params);
      setDocumentos(response.data.data || []);
    } catch (error) {
      console.error('Error cargando notas de débito:', error);
      toast.error('Error al cargar las notas de débito');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDocumentos();
  }, [filtroFecha]);

  const handleSearch = (e) => {
    e.preventDefault();
    cargarDocumentos();
  };

  const handleSendEmail = async () => {
    if (!selectedDoc || selectedDoc.status !== 'accepted') return;
    setSendingEmail(true);
    try {
      await documentsApi.sendEmail(selectedDoc.id);
      toast.success('Email enviado al cliente');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDownloadXml = async () => {
    if (!selectedDoc) return;
    try {
      const response = await documentsApi.downloadXml(selectedDoc.id);
      const contentType = response.headers['content-type'] || '';
      const isZip = contentType.includes('application/zip');
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedDoc.consecutivo}.${isZip ? 'zip' : 'xml'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al descargar XML');
    }
  };

  const totalesPorMoneda = documentos.reduce((acc, doc) => {
    if (doc.status === 'cancelled') return acc;
    const { moneda, subtotal, impuestos, total } = montos(doc);
    if (!acc[moneda]) acc[moneda] = { subtotal: 0, impuestos: 0, total: 0, count: 0 };
    acc[moneda].subtotal += subtotal;
    acc[moneda].impuestos += impuestos;
    acc[moneda].total += total;
    acc[moneda].count++;
    return acc;
  }, {});

  const btn = 'flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent';

  return (
    <div className="flex flex-col h-[var(--alto-contenido)] min-w-0">
      {/* Action Toolbar */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 shrink-0">
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setShowPrintPreview(true)} disabled={!selectedDoc}
            className={clsx(btn, 'border-slate-400 text-slate-600 hover:bg-slate-50 hover:border-slate-500')}>
            <Printer className="h-5 w-5 mb-1" />
            <span>Imprimir</span>
          </button>
          <button onClick={handleSendEmail} disabled={!selectedDoc || sendingEmail || selectedDoc?.status !== 'accepted'}
            className={clsx(btn, 'border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500')}>
            <Mail className="h-5 w-5 mb-1" />
            <span>{sendingEmail ? 'Enviando...' : 'Email'}</span>
          </button>
          <div className="w-px h-12 bg-gray-300 mx-1" />
          <button onClick={handleDownloadXml} disabled={!selectedDoc}
            className={clsx(btn, 'border-teal-400 text-teal-600 hover:bg-teal-50 hover:border-teal-500')}>
            <Download className="h-5 w-5 mb-1" />
            <span>XML</span>
          </button>
          <button onClick={() => setShowDetailModal(true)} disabled={!selectedDoc}
            className={clsx(btn, 'border-purple-400 text-purple-600 hover:bg-purple-50 hover:border-purple-500')}>
            <Eye className="h-5 w-5 mb-1" />
            <span>Detalles</span>
          </button>
        </div>
        {selectedDoc && (
          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-sm text-gray-600">
            <span>
              Seleccionado: <span className="font-mono font-medium text-gray-900">{selectedDoc.consecutivo}</span>
              {' '}- {selectedDoc.receptor_nombre || selectedDoc.receptorNombre || ''}
            </span>
            <button onClick={() => setSelectedDoc(null)} className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 shrink-0">
        <div className="flex flex-col md:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por consecutivo, cliente..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </form>
          <MonthRangePicker value={filtroFecha} onChange={setFiltroFecha} onRefresh={cargarDocumentos} />
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1 min-h-0 flex flex-col">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-2 text-gray-500">Cargando...</p>
          </div>
        ) : documentos.length === 0 ? (
          <div className="p-8 text-center">
            <FilePlus2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No hay notas de débito en este período</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 shadow-[0_1px_0_rgba(229,231,235,1)]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Consecutivo</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Fecha</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Cliente</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Doc. Referencia</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Motivo</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700 whitespace-nowrap">SubTotal</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Impuestos</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Total</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {documentos.map((doc) => {
                  const { moneda, subtotal, impuestos, total } = montos(doc);
                  const ref = doc.referencia || {};
                  const refTipo = ref.tipoDoc || doc.referencia_tipo_doc;
                  const refCons = ref.consecutivo || doc.referencia_consecutivo;
                  const refCodigo = ref.codigo || doc.referencia_codigo;
                  const isSelected = selectedDoc?.id === doc.id;
                  return (
                    <tr
                      key={doc.id}
                      onClick={() => setSelectedDoc(isSelected ? null : doc)}
                      className={clsx('cursor-pointer transition-colors', isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50')}
                    >
                      <td className="px-3 py-2 font-mono text-xs text-gray-900">{doc.consecutivo}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDate(doc.fecha_emision || doc.fechaEmision)}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">{doc.receptor_nombre || doc.receptorNombre || 'Sin cliente'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {refCons ? (
                          <span className="inline-flex items-center gap-1.5">
                            {refTipo && (
                              <span className={clsx(
                                'text-[10px] px-1.5 py-px rounded font-medium',
                                refTipo === '03' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                              )}>
                                {TIPO_DOC_REF[refTipo] || refTipo}
                              </span>
                            )}
                            <span className="font-mono text-xs text-gray-600">{refCons}</span>
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{CODIGO_REFERENCIA[refCodigo] || refCodigo || '-'}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{formatCurrency(subtotal, moneda)}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{formatCurrency(impuestos, moneda)}</td>
                      <td className="px-3 py-2 text-right font-bold text-gray-900">{formatCurrency(total, moneda)}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={clsx('inline-flex px-2 py-0.5 rounded-full text-xs font-medium', STATUS_CONFIG[doc.status]?.color || 'bg-gray-100 text-gray-700')}>
                          {STATUS_CONFIG[doc.status]?.label || doc.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="sticky bottom-0 z-10 bg-gray-100 border-t-2 border-gray-400">
                {Object.entries(totalesPorMoneda).map(([moneda, t]) => (
                  <tr key={moneda}>
                    <td colSpan={5} className="px-3 py-2 text-sm text-gray-600 font-medium">Total {moneda} ({t.count})</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-700">{formatCurrency(t.subtotal, moneda)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-700">{formatCurrency(t.impuestos, moneda)}</td>
                    <td className="px-3 py-2 text-right font-bold text-gray-900">{formatCurrency(t.total, moneda)}</td>
                    <td></td>
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {showPrintPreview && selectedDoc && (
        <PrintPreviewModal documentId={selectedDoc.id} emisor={currentEmisor} onClose={() => setShowPrintPreview(false)} />
      )}
      {showDetailModal && selectedDoc && (
        <DocumentDetailModal documentId={selectedDoc.id} emisor={currentEmisor} onClose={() => setShowDetailModal(false)} />
      )}
    </div>
  );
}
